/**
 * Directional motion on route change, so moving through the app reads as depth rather than as the
 * screen being replaced instantly.
 *
 * HONEST SCOPE: this is a directional CUE, not a true iOS push. A real push slides the incoming
 * screen over the outgoing one, which needs a snapshot of the old screen — the View Transitions API
 * does that, but it is Safari 18+ only and its behaviour under React 18's async rendering is
 * exactly the kind of thing that cannot be judged without a device in hand. A short offset-and-fade
 * in the direction of travel conveys the same forward/back relationship, degrades to nothing when
 * unsupported, and cannot look broken.
 *
 * Driven with the Web Animations API rather than a `key` on the route element: keying would REMOUNT
 * the page on every navigation, discarding component state, cancelling in-flight requests and
 * re-running every query — a heavy price for an animation.
 */
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { getNavDirection, supportsViewTransitions } from './nav-history';

const DURATION_MS = 260;
/** Small on purpose: a large slide with no outgoing screen behind it reads as a glitch. */
const OFFSET_PX = 20;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface RouteTransitionOptions {
  surfaceRef: React.RefObject<HTMLElement | null>;
  /** Pass false on desktop — sliding panes are a phone idiom, and the rail already frames changes. */
  enabled: boolean;
}

export function useRouteTransition({ surfaceRef, enabled }: RouteTransitionOptions): void {
  const { pathname } = useLocation();
  const previousPath = React.useRef<string | null>(null);

  React.useEffect(() => {
    const first = previousPath.current === null;
    const changed = previousPath.current !== pathname;
    previousPath.current = pathname;

    // Never animate the first paint: there was no navigation, and animating the app's own cold
    // start makes launch feel slower, which is the opposite of the goal.
    if (first || !changed || !enabled) return;
    if (prefersReducedMotion()) return;
    // Where the browser can snapshot the outgoing screen, the View Transitions push in globals.css
    // is doing the work — running this fade on top would animate the incoming screen twice, once
    // inside the transition snapshot and once after it.
    if (supportsViewTransitions()) return;

    const surface = surfaceRef.current;
    if (!surface || typeof surface.animate !== 'function') return;

    const back = getNavDirection() === 'back';
    const from = back ? -OFFSET_PX : OFFSET_PX;

    const animation = surface.animate(
      [
        { transform: `translateX(${from}px)`, opacity: 0.35 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { duration: DURATION_MS, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', fill: 'both' },
    );

    return () => {
      // A navigation landing mid-animation must not leave the surface offset or faded. `finish()`
      // jumps to the end state (transform none, opacity 1) rather than reverting to the start.
      if (animation.playState !== 'finished') animation.finish();
      animation.cancel();
    };
  }, [pathname, enabled, surfaceRef]);
}
